import '../index.scss';
import React, { Component, useState } from 'react';
import { Connection, Port, RootState } from '..';
import { PortFormEditor } from '../Geometry/PortFormEditor';
import produce from "immer"
import { Button, Card, Form, InputGroup } from 'react-bootstrap';
import { AiOutlineColumnWidth } from "react-icons/ai";
import { RiDeleteBin2Line, RiRouteLine } from 'react-icons/ri';
import { FormControlLabel, Switch, MuiThemeProvider, createMuiTheme } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { Microfluidics } from '../Utils/Microfluidics';


export { ConnectionDetails }

function ConnectionDetails(props: {
  connection: Connection
  onChange: (c: Connection) => void
  onDelete?: () => void
}) {

  const changePort = (index: number) => {
    return (port: Port) => {
      const connection = produce(props.connection, (draft: Connection) => {
        draft.ports[index] = port
      })
      props.onChange(connection)
    }
  }

  const output = useSelector((state: RootState) => state.output)
  const routingResult = output.channels[props.connection.id]

  const settings = useSelector((state: RootState) => state.settings)

  const microfluidicInfoDefault = {
    resistance: undefined,
    height: 50,
    viscosity: 1e-3
  }

  const [microfluidicInfo, setMicrofluidicInfo] = useState({
    resistance: props.connection.modifyLength && props.connection.length !== undefined ? Microfluidics.resistanceMicro({
      width: props.connection.width,
      height: microfluidicInfoDefault.height,
      length: props.connection.length,
      viscosity: microfluidicInfoDefault.viscosity,
    }) : undefined,
    height: microfluidicInfoDefault.height,
    viscosity: microfluidicInfoDefault.viscosity
  })

  return (<Card
      className='connectionDetails'
    >
    <Card.Header>
      <RiRouteLine /> Connection
      { props.onDelete !== undefined &&
        <Button
          onClick={props.onDelete}
          variant="outline-remove-dark"
          style={{
            marginLeft: '1em'
          }}
        >
          <RiDeleteBin2Line /> Remove
        </Button>
      }
    </Card.Header>
    <Card.Body
      style={{
        paddingTop: 0
      }}
    >
    <Card.Text>
    { routingResult !== undefined && routingResult.channel !== undefined &&
      <span>Channel Length: {routingResult.channel.length.toFixed(3)} µm</span>
    }
    { routingResult !== undefined && routingResult.channel === undefined &&
      <span>Channel Length: -</span>
    }
    </Card.Text>

      <h5
        style={{
          marginBottom: 0
        }}
      >
        Port A
      </h5>
      <div className="cardSection">
        <PortFormEditor
          port={props.connection.ports[0]}
          onChange={changePort(0)}
          inline={true}
        />
      </div>

      <h5
        style={{
          marginBottom: 0
        }}
      >
        Port B
      </h5>
      <div className="cardSection">
        <PortFormEditor
          port={props.connection.ports[1]}
          onChange={changePort(1)}
          inline={true}
        />
      </div>

    <div className="separator">Channel Specifications</div>

    <Form inline>
    <InputGroup
      style={{margin: '1em'}}
    >
      <InputGroup.Prepend>
        <InputGroup.Text>
          <AiOutlineColumnWidth /> <span>&nbsp;</span> Width
        </InputGroup.Text>
      </InputGroup.Prepend>
      <Form.Control
        type="number"
        placeholder="Width"
        aria-label="Width"
        value={ Number.isNaN(props.connection.width) ? '' : props.connection.width }
        onChange={ (e) => props.onChange(produce(props.connection, (draft: Connection) => {
            draft.width = parseFloat(e.target.value)
          })) }
        style={{ maxWidth: '4.2em' }}
      />
      <InputGroup.Append>
        <InputGroup.Text>µm</InputGroup.Text>
      </InputGroup.Append>
    </InputGroup>

    <InputGroup
      style={{margin: '1em'}}
    >
      <InputGroup.Prepend>
        <InputGroup.Text>
          <AiOutlineColumnWidth /> <span>&nbsp;</span> Spacing
        </InputGroup.Text>
      </InputGroup.Prepend>
      <Form.Control
        type="number"
        placeholder="Spacing"
        aria-label="Spacing"
        value={ Number.isNaN(props.connection.spacing) ? '' : props.connection.spacing }
        onChange={ (e) => props.onChange(produce(props.connection, (draft: Connection) => {
            draft.spacing = parseFloat(e.target.value)
          })) }
        style={{ maxWidth: '4.2em' }}
      />
      <InputGroup.Append>
        <InputGroup.Text>µm</InputGroup.Text>
      </InputGroup.Append>
    </InputGroup>

    <InputGroup
      style={{
        margin: '1em',
      }}
    >
      <InputGroup.Prepend>
        <InputGroup.Text>
          <AiOutlineColumnWidth /> <span>&nbsp;</span> Bend Radius
        </InputGroup.Text>
      </InputGroup.Prepend>
      <Form.Control
        type="number"
        placeholder="Bend Radius"
        aria-label="Minimum Bend Radius"
        value={ Number.isNaN(props.connection.minBendRadius) ? '' : props.connection.minBendRadius }
        onChange={ (e) => props.onChange(produce(props.connection, (draft: Connection) => {
            draft.minBendRadius = parseFloat(e.target.value)
          })) }
        style={{ maxWidth: '4.2em' }}
      />
      <InputGroup.Append>
        <InputGroup.Text>µm</InputGroup.Text>
      </InputGroup.Append>
    </InputGroup>
    <MuiThemeProvider theme={createMuiTheme({
      palette: {
        primary: {
          main: '#222',
        },
        secondary: {
          main: settings.connectionDefaultColor,
        },
      },
    })}>
    <FormControlLabel
      control={
        <Switch
          checked={props.connection.modifyLength}
          onChange={(e) => {
            props.onChange(produce(props.connection, (draft: Connection) => {
              draft.modifyLength = e.target.checked
            }))
          }}
          color="primary"
        />
      }
      label="Modify Length"
      style={{
        marginLeft: '1em'
      }}
    />
    </MuiThemeProvider>
    </Form>

    { props.connection.modifyLength &&
    <React.Fragment>
    <div className="separator">Specify Length</div>
    <InputGroup
      style={{margin: '1em'}}
    >
      <InputGroup.Prepend>
        <InputGroup.Text>
          <AiOutlineColumnWidth /> <span>&nbsp;</span> Length
        </InputGroup.Text>
      </InputGroup.Prepend>
      <Form.Control
        type="number"
        placeholder="Desired Length"
        aria-label="Desired Length"
        value={ Number.isNaN(props.connection.length) ? '' : props.connection.length }
        onChange={ (e) => {
            const length = parseFloat(e.target.value)

            const resistance = Microfluidics.resistanceMicro({
              width: props.connection.width,
              length,
              height: microfluidicInfo.height,
              viscosity: microfluidicInfo.viscosity
            })
            setMicrofluidicInfo({
              resistance: resistance,
              height: microfluidicInfo.height,
              viscosity: microfluidicInfo.viscosity
            })
            props.onChange(produce(props.connection, (draft: Connection) => {
              draft.length = parseFloat(e.target.value)
            }))
          }
        }
        style={{ maxWidth: '9em' }}
      />
      <InputGroup.Append>
        <InputGroup.Text>µm</InputGroup.Text>
      </InputGroup.Append>
    </InputGroup>

    <div className="separator">or Resistance</div>

    <Form inline>
    <InputGroup
      style={{margin: '1em'}}
    >
    <InputGroup.Prepend>
      <InputGroup.Text>
       Resistance
      </InputGroup.Text>
    </InputGroup.Prepend>
    <Form.Control
      type="number"
      placeholder="Resistance"
      aria-label="Resistance"
      value={microfluidicInfo.resistance ?? 0}
      onChange={(e) => {
          let resistance = parseFloat(e.target.value)
          if(Number.isNaN(resistance)) {
            resistance = 0
          }
          setMicrofluidicInfo({
            resistance: resistance,
            height: microfluidicInfo.height,
            viscosity: microfluidicInfo.viscosity
          })

          const length = Microfluidics.lengthFromResistanceMicro({
            width: props.connection.width,
            resistance: resistance,
            height: microfluidicInfo.height,
            viscosity: microfluidicInfo.viscosity
          })
          props.onChange(produce(props.connection, (draft: Connection) => {
            draft.length = length
          }))
        }
      }
      style={{ maxWidth: '14em' }}
    />
    <InputGroup.Append>
      <InputGroup.Text>kg / (m^4 s)</InputGroup.Text>
    </InputGroup.Append>
  </InputGroup>

  <InputGroup
    style={{margin: '1em'}}
  >
  <InputGroup.Prepend>
    <InputGroup.Text>
      Channel Height
    </InputGroup.Text>
  </InputGroup.Prepend>
  <Form.Control
    type="number"
    placeholder="Channel height"
    aria-label="Channel height"
    value={microfluidicInfo.height}
    onChange={(e) => {
      let height = parseFloat(e.target.value)
        if(Number.isNaN(height)) {
          height = 0
        }
        const resistance = microfluidicInfo.resistance

        setMicrofluidicInfo({
          resistance,
          height,
          viscosity: microfluidicInfo.viscosity
        })

        const length = Microfluidics.lengthFromResistanceMicro({
          width: props.connection.width,
          resistance: resistance ?? 0,
          height,
          viscosity: microfluidicInfo.viscosity
        })

        props.onChange(produce(props.connection, (draft: Connection) => {
          draft.length = length
        }))
      }
    }
    style={{ maxWidth: '8em' }}
  />
  <InputGroup.Append>
    <InputGroup.Text>µm</InputGroup.Text>
  </InputGroup.Append>
</InputGroup>

<InputGroup
  style={{margin: '1em'}}
>
<InputGroup.Prepend>
  <InputGroup.Text>
     Viscosity
  </InputGroup.Text>
</InputGroup.Prepend>
<Form.Control
  type="number"
  placeholder="Fluid Viscosity"
  aria-label="Fluid Viscosity"
  value={microfluidicInfo.viscosity}
  onChange={(e) => {
    let viscosity = parseFloat(e.target.value)
      if(Number.isNaN(viscosity)) {
        viscosity = 0
      }
      const resistance = microfluidicInfo.resistance

      setMicrofluidicInfo({
        resistance,
        height: microfluidicInfo.height,
        viscosity
      })

      const length = Microfluidics.lengthFromResistanceMicro({
        width: props.connection.width,
        resistance: resistance ?? 0,
        height: microfluidicInfo.height,
        viscosity: viscosity
      })

      props.onChange(produce(props.connection, (draft: Connection) => {
        draft.length = length
      }))
    }
  }
  style={{ maxWidth: '8em' }}
/>
<InputGroup.Append>
  <InputGroup.Text>kg / (m s)</InputGroup.Text>
</InputGroup.Append>
</InputGroup>
</Form>

    </React.Fragment>
    }
    </Card.Body>
    </Card>
  )
}
